import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { signal } from '@preact/signals-core';
import { html, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import { type DataViewI18nKey, dataViewI18nT } from '../../../../core/i18n.js';
import {
  wizardActionsStyle,
  wizardAdvancedHeaderTextStyle,
  wizardAdvancedTriggerStyle,
  wizardBackdropStyle,
  wizardChoiceCardStyle,
  wizardChoiceRowStyle,
  wizardContainedBackdropStyle,
  wizardContainedDialogStyle,
  wizardCreateCancelStyle,
  wizardCreateInputRowStyle,
  wizardCreateInputStyle,
  wizardDescriptionStyle,
  wizardDialogStyle,
  wizardDisclosureButtonStyle,
  wizardExpandableBodyStyle,
  wizardExpandableInnerStyle,
  wizardFieldStyle,
  wizardFoundBadgeStyle,
  wizardHeroStyle,
  wizardHintStyle,
  wizardInlineContainerStyle,
  wizardLabelStyle,
  wizardPrimaryButtonStyle,
  wizardSecondaryButtonStyle,
  wizardSectionHeaderStyle,
  wizardSectionHintStyle,
  wizardSectionStyle,
  wizardSectionTitleStyle,
  wizardSelectPlaceholderStyle,
  wizardSelectStyle,
  wizardTitleStyle,
  wizardTrackingRowStyle,
} from '../../../shared/setup-wizard/setup-wizard-css.js';
import type {
  CalendarColumnRole,
  CalendarSingleView,
} from '../../calendar-view-manager.js';
import { calendarRoleAcceptsPropertyType } from '../../calendar-view-manager.js';
import {
  CalendarColumnDetectionService,
  type CalendarDetectedColumns,
  type CalendarSetupProperty,
  calendarSetupPropertyReadonly,
} from './calendar-column-detection-service.js';

export const CALENDAR_SETUP_WIZARD_LAYOUT_CHANGE_EVENT =
  'calendar-setup-wizard-layout-change';

type Mode = 'auto' | 'existing';
type ValueRef<T> = { value: T };

type CalendarRoleOption = {
  description: string;
  label: string;
  name: string;
  role: CalendarColumnRole;
  type: string;
};

type CalendarRoleMeta = Pick<CalendarRoleOption, 'role' | 'type'>;

export interface CalendarWizardStrings {
  readonly title: string;
  readonly description: string;
  readonly datesTitle: string;
  readonly datesAutoHint: string;
  readonly dateExistingHint: string;
  readonly startLabel: string;
  readonly endLabel: string;
  readonly oneDateHint: (columnName: string) => string;
  readonly roleStart: string;
  readonly roleEnd: string;
  readonly missingColumnNote: (missing: string) => string;
  readonly automaticSummary: string;
  readonly advancedTitle: string;
  readonly advancedDescription: string;
  readonly detailsTitle: string;
  readonly detailsDescription: string;
  readonly detailsAutoHint: string;
  readonly detailsCollapsedHint: string;
  readonly recurrenceTitle: string;
  readonly recurrenceDescription: string;
  readonly recurrenceCollapsedHint: string;
  readonly statusTitle: string;
  readonly statusDescription: string;
  readonly statusCollapsedHint: string;
  readonly chooseOrCreatePlaceholder: string;
  readonly createNewColumn: string;
  readonly autoCreateLabel: (defaultName: string) => string;
  readonly foundBadge: string;
  readonly matchedSuffix: string;
  readonly newColumnNamePlaceholder: string;
  readonly cancel: string;
  readonly apply: string;
  readonly getStarted: string;
}

const defaultCalendarWizardStrings = (): CalendarWizardStrings => ({
  title: dataViewI18nT('calendar.wizard.title'),
  description: dataViewI18nT('calendar.wizard.description'),
  datesTitle: dataViewI18nT('calendar.wizard.datesTitle'),
  datesAutoHint: dataViewI18nT('calendar.wizard.datesAutoHint'),
  dateExistingHint: dataViewI18nT('calendar.wizard.dateExistingHint'),
  startLabel: dataViewI18nT('calendar.wizard.startLabel'),
  endLabel: dataViewI18nT('calendar.wizard.endLabel'),
  oneDateHint: name =>
    dataViewI18nT('calendar.wizard.oneDateHint', { column: name }),
  roleStart: dataViewI18nT('calendar.wizard.roleStart'),
  roleEnd: dataViewI18nT('calendar.wizard.roleEnd'),
  missingColumnNote: missing =>
    dataViewI18nT('calendar.wizard.missingColumnNote', { missing }),
  automaticSummary: dataViewI18nT('calendar.wizard.automaticSummary'),
  advancedTitle: dataViewI18nT('calendar.wizard.advancedTitle'),
  advancedDescription: dataViewI18nT('calendar.wizard.advancedDescription'),
  detailsTitle: dataViewI18nT('calendar.wizard.detailsTitle'),
  detailsDescription: dataViewI18nT('calendar.wizard.detailsDescription'),
  detailsAutoHint: dataViewI18nT('calendar.wizard.detailsAutoHint'),
  detailsCollapsedHint: dataViewI18nT('calendar.wizard.detailsCollapsedHint'),
  recurrenceTitle: dataViewI18nT('calendar.wizard.recurrenceTitle'),
  recurrenceDescription: dataViewI18nT('calendar.wizard.recurrenceDescription'),
  recurrenceCollapsedHint: dataViewI18nT(
    'calendar.wizard.recurrenceCollapsedHint'
  ),
  statusTitle: dataViewI18nT('calendar.wizard.statusTitle'),
  statusDescription: dataViewI18nT('calendar.wizard.statusDescription'),
  statusCollapsedHint: dataViewI18nT('calendar.wizard.statusCollapsedHint'),
  chooseOrCreatePlaceholder: dataViewI18nT(
    'calendar.wizard.chooseOrCreatePlaceholder'
  ),
  createNewColumn: dataViewI18nT('calendar.wizard.createNewColumn'),
  autoCreateLabel: defaultName =>
    dataViewI18nT('calendar.wizard.autoCreateLabel', { defaultName }),
  foundBadge: dataViewI18nT('calendar.wizard.foundBadge'),
  matchedSuffix: dataViewI18nT('calendar.wizard.matchedSuffix'),
  newColumnNamePlaceholder: dataViewI18nT(
    'calendar.wizard.newColumnNamePlaceholder'
  ),
  cancel: dataViewI18nT('calendar.action.cancel'),
  apply: dataViewI18nT('calendar.action.apply'),
  getStarted: dataViewI18nT('calendar.action.getStarted'),
});

const ROLE_CONFIG = {
  allDay: {
    role: 'allDay',
    type: 'checkbox',
  },
  attendees: {
    role: 'attendees',
    type: 'rich-text',
  },
  calendarId: {
    role: 'calendarId',
    type: 'select',
  },
  description: {
    role: 'description',
    type: 'rich-text',
  },
  end: {
    role: 'end',
    type: 'date',
  },
  locationPhysical: {
    role: 'locationPhysical',
    type: 'rich-text',
  },
  locationVirtual: {
    role: 'locationVirtual',
    type: 'link',
  },
  recurrence: {
    role: 'recurrence',
    type: 'rich-text',
  },
  recurrenceExdate: {
    role: 'recurrenceExdate',
    type: 'rich-text',
  },
  recurrenceId: {
    role: 'recurrenceId',
    type: 'rich-text',
  },
  reminder: {
    role: 'reminder',
    type: 'rich-text',
  },
  start: {
    role: 'start',
    type: 'date',
  },
  status: {
    role: 'status',
    type: 'select',
  },
  timezone: {
    role: 'timezone',
    type: 'rich-text',
  },
  visibility: {
    role: 'visibility',
    type: 'select',
  },
} satisfies Record<CalendarColumnRole, CalendarRoleMeta>;

export const CALENDAR_SETUP_REQUIRED_ROLES: readonly CalendarRoleMeta[] = [
  ROLE_CONFIG.start,
  ROLE_CONFIG.end,
];

export const CALENDAR_SETUP_OPTIONAL_ROLES: readonly CalendarRoleMeta[] = [
  ROLE_CONFIG.allDay,
  ROLE_CONFIG.timezone,
  ROLE_CONFIG.calendarId,
  ROLE_CONFIG.recurrence,
  ROLE_CONFIG.recurrenceExdate,
  ROLE_CONFIG.recurrenceId,
  ROLE_CONFIG.locationPhysical,
  ROLE_CONFIG.locationVirtual,
  ROLE_CONFIG.attendees,
  ROLE_CONFIG.reminder,
  ROLE_CONFIG.visibility,
  ROLE_CONFIG.status,
  ROLE_CONFIG.description,
];

const DETAIL_ROLES: readonly CalendarColumnRole[] = [
  'allDay',
  'calendarId',
  'locationPhysical',
  'locationVirtual',
  'attendees',
  'description',
];

const RECURRENCE_ROLES: readonly CalendarColumnRole[] = [
  'timezone',
  'recurrence',
  'recurrenceExdate',
  'recurrenceId',
  'reminder',
];

const STATUS_ROLES: readonly CalendarColumnRole[] = ['status', 'visibility'];

export function calendarSetupRoleAcceptsProperty(
  role: CalendarColumnRole,
  propertyType: string
): boolean {
  return calendarRoleAcceptsPropertyType(role, propertyType);
}

export function calendarSetupPropertiesForRole(
  view: CalendarSingleView,
  role: CalendarColumnRole
): CalendarSetupProperty[] {
  return view
    .getBoundedRawPropertiesWindow()
    .properties.filter(
      property =>
        !calendarSetupPropertyReadonly(property) &&
        calendarSetupRoleAcceptsProperty(role, property.type$.value)
    );
}

export class CalendarSetupWizard extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor contained = false;

  @property({ attribute: false })
  accessor inline = false;

  @property({ attribute: false })
  accessor onClose: (() => void) | undefined;

  @property({ attribute: false })
  accessor strings: Partial<CalendarWizardStrings> = {};

  @property({ attribute: false })
  accessor view!: CalendarSingleView;

  private readonly advancedExpanded = signal(false);
  private readonly datesMode = signal<Mode>('auto');
  private readonly datesExpanded = signal(false);
  private readonly detailsExpanded = signal(false);
  private readonly recurrenceExpanded = signal(false);
  private readonly singleDateRole = signal<'start' | 'end'>('start');
  private readonly statusExpanded = signal(false);
  private detectedColumnsSnapshot: CalendarDetectedColumns | undefined;
  private layoutChangeFrame: number | undefined;

  private readonly creating = {
    allDay: signal<string | null>(null),
    attendees: signal<string | null>(null),
    calendarId: signal<string | null>(null),
    description: signal<string | null>(null),
    end: signal<string | null>(null),
    locationPhysical: signal<string | null>(null),
    locationVirtual: signal<string | null>(null),
    recurrence: signal<string | null>(null),
    recurrenceExdate: signal<string | null>(null),
    recurrenceId: signal<string | null>(null),
    reminder: signal<string | null>(null),
    start: signal<string | null>(null),
    status: signal<string | null>(null),
    timezone: signal<string | null>(null),
    visibility: signal<string | null>(null),
  } satisfies Record<CalendarColumnRole, ValueRef<string | null>>;

  private readonly picked = {
    allDay: signal<string | undefined>(undefined),
    attendees: signal<string | undefined>(undefined),
    calendarId: signal<string | undefined>(undefined),
    description: signal<string | undefined>(undefined),
    end: signal<string | undefined>(undefined),
    locationPhysical: signal<string | undefined>(undefined),
    locationVirtual: signal<string | undefined>(undefined),
    recurrence: signal<string | undefined>(undefined),
    recurrenceExdate: signal<string | undefined>(undefined),
    recurrenceId: signal<string | undefined>(undefined),
    reminder: signal<string | undefined>(undefined),
    start: signal<string | undefined>(undefined),
    status: signal<string | undefined>(undefined),
    timezone: signal<string | undefined>(undefined),
    visibility: signal<string | undefined>(undefined),
  } satisfies Record<CalendarColumnRole, ValueRef<string | undefined>>;

  override connectedCallback(): void {
    super.connectedCallback();
    const detected = CalendarColumnDetectionService.scan(this.view);
    this.detectedColumnsSnapshot = detected;
    this.seedFromView(detected);
    this.disposables.addFromEvent(this, 'keydown', event => {
      if ((event as KeyboardEvent).key === 'Escape') this.close();
    });
  }

  protected override updated(): void {
    this.queueLayoutChange();
  }

  override disconnectedCallback(): void {
    if (this.layoutChangeFrame !== undefined) {
      cancelAnimationFrame(this.layoutChangeFrame);
      this.layoutChangeFrame = undefined;
    }
    super.disconnectedCallback();
  }

  override render(): TemplateResult {
    const t = this.t();
    if (this.inline) {
      return html`
        <section
          class=${wizardInlineContainerStyle}
          data-data-view-body-content="true"
          data-data-view-body-intrinsic="true"
          data-data-view-body-render-content="true"
          data-data-view-body-render-root="true"
          role="region"
          aria-label=${t.title}
        >
          ${this.renderContent(t)}
        </section>
      `;
    }

    const containerStyle = this.contained
      ? wizardContainedBackdropStyle
      : wizardBackdropStyle;
    const dialogStyle = this.contained
      ? `${wizardDialogStyle} ${wizardContainedDialogStyle}`
      : wizardDialogStyle;
    return html`
      <div class=${containerStyle}>
        <div
          class=${dialogStyle}
          role="dialog"
          aria-modal="true"
          aria-label=${t.title}
          @click=${(event: Event) => event.stopPropagation()}
        >
          ${this.renderContent(t)}
        </div>
      </div>
    `;
  }

  private renderContent(t: CalendarWizardStrings): TemplateResult {
    return html`
      <header class=${wizardHeroStyle}>
        <h1 class=${wizardTitleStyle}>${t.title}</h1>
        <p class=${wizardDescriptionStyle}>${t.description}</p>
      </header>

      <p class=${wizardHintStyle}>${t.automaticSummary}</p>
      ${this.renderAdvancedSettings(t)}

      <div class=${wizardActionsStyle}>
        <button
          class=${wizardSecondaryButtonStyle}
          type="button"
          @click=${this.close}
        >
          ${t.cancel}
        </button>
        <button
          class=${wizardPrimaryButtonStyle}
          type="button"
          ?disabled=${!this.canApply()}
          @click=${() => this.apply()}
        >
          ${this.hasAnyDateColumns() ? t.apply : t.getStarted}
        </button>
      </div>
    `;
  }

  private apply(): void {
    if (this.view.readonly$.value) return;
    this.applyDates();
    this.applyRoles(DETAIL_ROLES);
    this.applyRoles(RECURRENCE_ROLES);
    this.applyRoles(STATUS_ROLES);
    this.close();
  }

  private applyDates(): void {
    const dateColumns = this.dateColumns();
    if (this.datesMode.value === 'auto' || dateColumns.length === 0) {
      this.setOrCreateRole('start');
      this.setOrCreateRole('end');
      return;
    }
    if (dateColumns.length === 1) {
      const existing = dateColumns[0];
      if (!existing) return;
      if (this.singleDateRole.value === 'start') {
        this.view.setStartDateCol(existing.id);
        this.createRoleColumn('end');
      } else {
        this.view.setEndDateCol(existing.id);
        this.createRoleColumn('start');
      }
      return;
    }
    this.setOrCreateRole('start');
    this.setOrCreateRole('end');
  }

  private applyRoles(roles: readonly CalendarColumnRole[]): void {
    for (const role of roles) {
      this.setOrCreateRole(role);
    }
  }

  private canApply(): boolean {
    if (this.view.readonly$.value) return false;
    if (this.datesMode.value === 'auto') return true;
    const dateColumns = this.dateColumns();
    if (dateColumns.length === 0) return false;
    if (dateColumns.length === 1) return true;
    if (!this.pickerReady('start') || !this.pickerReady('end')) return false;
    const start = this.picked.start.value;
    const end = this.picked.end.value;
    return !start || !end || start !== end;
  }

  private readonly close = (): void => {
    this.onClose?.();
    this.dispatchLayoutChange();
  };

  private createRoleColumn(
    role: CalendarColumnRole,
    name = this.roleOption(role).name
  ): string | undefined {
    const id = this.view.propertyAdd('end', {
      name,
      type: ROLE_CONFIG[role].type,
    });
    if (id) this.view.setRoleColumn(role, id);
    return id;
  }

  private detectedColumns(): CalendarDetectedColumns {
    this.detectedColumnsSnapshot ??= CalendarColumnDetectionService.scan(
      this.view
    );
    return this.detectedColumnsSnapshot;
  }

  private dateColumns(): CalendarSetupProperty[] {
    return [...this.detectedColumns().byRole.start];
  }

  private dispatchLayoutChange(): void {
    this.dispatchEvent(
      new CustomEvent(CALENDAR_SETUP_WIZARD_LAYOUT_CHANGE_EVENT, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private queueLayoutChange(): void {
    if (this.layoutChangeFrame !== undefined) return;
    this.layoutChangeFrame = requestAnimationFrame(() => {
      this.layoutChangeFrame = undefined;
      this.dispatchLayoutChange();
    });
  }

  private hasAnyDateColumns(): boolean {
    return this.dateColumns().length > 0;
  }

  private isMatched(role: CalendarColumnRole, propertyId: string | undefined) {
    if (!propertyId) return false;
    return this.detectedColumns().matches[role]?.id === propertyId;
  }

  private pickerReady(role: CalendarColumnRole): boolean {
    return (
      this.creating[role].value !== null || Boolean(this.picked[role].value)
    );
  }

  private renderCreateInput(
    role: CalendarColumnRole,
    label: string,
    t: CalendarWizardStrings
  ): TemplateResult {
    const creating = this.creating[role];
    return html`
      <div class=${wizardFieldStyle}>
        <label class=${wizardLabelStyle}>${label}</label>
        <div class=${wizardCreateInputRowStyle}>
          <input
            class=${wizardCreateInputStyle}
            type="text"
            autofocus
            .value=${creating.value ?? ''}
            placeholder=${t.newColumnNamePlaceholder}
            @input=${(event: Event) => {
              creating.value = (event.target as HTMLInputElement).value;
            }}
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                creating.value = null;
              }
            }}
          />
          <button
            class=${wizardCreateCancelStyle}
            type="button"
            aria-label=${t.cancel}
            @click=${() => {
              creating.value = null;
            }}
          >
            x
          </button>
        </div>
      </div>
    `;
  }

  private renderDatesSection(t: CalendarWizardStrings): TemplateResult {
    const dateColumns = this.dateColumns();
    const hasDates = dateColumns.length > 0;
    const expanded = this.datesExpanded.value;
    return html`
      <section class=${wizardSectionStyle}>
        ${this.renderDisclosureHeader(t.datesTitle, {
          description: expanded ? t.dateExistingHint : undefined,
          disabled: false,
          expanded,
          onToggle: () => {
            this.datesExpanded.value = !expanded;
            if (!expanded && hasDates) this.datesMode.value = 'existing';
            this.queueLayoutChange();
          },
        })}
        ${expanded
          ? nothing
          : html`<p class=${wizardHintStyle}>${t.datesAutoHint}</p>`}
        ${this.renderExpandableBody(expanded, () =>
          hasDates && this.datesMode.value === 'existing'
            ? this.renderExistingDatesBody(dateColumns, t)
            : this.renderAutoDatesBody(t)
        )}
      </section>
    `;
  }

  private renderAdvancedSettings(t: CalendarWizardStrings): TemplateResult {
    const expanded = this.advancedExpanded.value;
    return html`
      <button
        class=${wizardAdvancedTriggerStyle}
        type="button"
        aria-expanded=${expanded ? 'true' : 'false'}
        aria-label=${expanded
          ? dataViewI18nT('calendar.wizard.advancedCollapse', {
              title: t.advancedTitle,
            })
          : dataViewI18nT('calendar.wizard.advancedExpand', {
              title: t.advancedTitle,
            })}
        @click=${() => {
          this.advancedExpanded.value = !expanded;
          this.queueLayoutChange();
        }}
      >
        <span class=${wizardAdvancedHeaderTextStyle}>
          <span class=${wizardSectionTitleStyle}>${t.advancedTitle}</span>
          <span class=${wizardSectionHintStyle}>${t.advancedDescription}</span>
        </span>
        <span
          class=${wizardDisclosureButtonStyle}
          data-open=${expanded ? 'true' : 'false'}
          aria-hidden="true"
        >
          ${this.renderDisclosureChevronIcon()}
        </span>
      </button>
      ${this.renderExpandableBody(
        expanded,
        () => html`
          ${this.renderDatesSection(t)}
          ${this.renderRoleGroup(
            t.detailsTitle,
            t.detailsDescription,
            DETAIL_ROLES,
            this.detailsExpanded,
            t.detailsCollapsedHint,
            t.detailsAutoHint,
            t
          )}
          ${this.renderRoleGroup(
            t.recurrenceTitle,
            t.recurrenceDescription,
            RECURRENCE_ROLES,
            this.recurrenceExpanded,
            t.recurrenceCollapsedHint,
            undefined,
            t
          )}
          ${this.renderRoleGroup(
            t.statusTitle,
            t.statusDescription,
            STATUS_ROLES,
            this.statusExpanded,
            t.statusCollapsedHint,
            undefined,
            t
          )}
        `
      )}
    `;
  }

  private renderAutoDatesBody(t: CalendarWizardStrings): TemplateResult {
    return html`
      ${this.renderRolePicker('start', true, t)}
      ${this.renderRolePicker('end', true, t)}
    `;
  }

  private renderExistingDatesBody(
    dateColumns: readonly CalendarSetupProperty[],
    t: CalendarWizardStrings
  ): TemplateResult {
    const only = dateColumns[0];
    if (dateColumns.length === 1 && only) {
      const role = this.singleDateRole.value;
      const missing = role === 'start' ? t.roleEnd : t.roleStart;
      return html`
        <p class=${wizardSectionHintStyle}>
          ${t.oneDateHint(only.name$.value)}
        </p>
        <div class=${wizardChoiceRowStyle}>
          <label
            class=${wizardChoiceCardStyle}
            data-selected=${role === 'start'}
          >
            <input
              type="radio"
              name="calendar-single-date-role"
              value="start"
              ?checked=${role === 'start'}
              @change=${() => {
                this.singleDateRole.value = 'start';
              }}
            />
            <span>${t.roleStart}</span>
          </label>
          <label class=${wizardChoiceCardStyle} data-selected=${role === 'end'}>
            <input
              type="radio"
              name="calendar-single-date-role"
              value="end"
              ?checked=${role === 'end'}
              @change=${() => {
                this.singleDateRole.value = 'end';
              }}
            />
            <span>${t.roleEnd}</span>
          </label>
        </div>
        <p class=${wizardSectionHintStyle}>${t.missingColumnNote(missing)}</p>
      `;
    }
    return html`
      ${this.renderRolePicker('start', false, t)}
      ${this.renderRolePicker('end', false, t)}
    `;
  }

  private renderRoleGroup(
    title: string,
    description: string,
    roles: readonly CalendarColumnRole[],
    expanded: ValueRef<boolean>,
    collapsedHint: string,
    expandedHint: string | undefined,
    t: CalendarWizardStrings
  ): TemplateResult {
    return html`
      <section class=${wizardSectionStyle}>
        ${this.renderDisclosureHeader(title, {
          description,
          disabled: false,
          expanded: expanded.value,
          onToggle: () => {
            expanded.value = !expanded.value;
            this.queueLayoutChange();
          },
        })}
        ${expanded.value
          ? nothing
          : html`<p class=${wizardHintStyle}>${collapsedHint}</p>`}
        ${this.renderExpandableBody(
          expanded.value,
          () => html`
            ${expandedHint
              ? html`<p class=${wizardHintStyle}>${expandedHint}</p>`
              : nothing}
            ${roles.map(role => this.renderRolePicker(role, true, t))}
          `
        )}
      </section>
    `;
  }

  private renderRolePicker(
    role: CalendarColumnRole,
    allowAuto: boolean,
    t: CalendarWizardStrings
  ): TemplateResult {
    const option = this.roleOption(role);
    const picked = this.picked[role].value;
    const creating = this.creating[role].value;
    const properties = this.pickerPropertiesForRole(role);
    if (creating !== null) {
      return this.renderCreateInput(role, option.label, t);
    }
    const selectedLabel = picked
      ? (properties.find(property => property.id === picked)?.name$.value ??
        t.chooseOrCreatePlaceholder)
      : t.autoCreateLabel(option.name);
    const muted = !picked;
    return html`
      <div class=${wizardTrackingRowStyle}>
        <div class=${wizardFieldStyle}>
          <label class=${wizardLabelStyle}>
            <span>${option.label}</span>
            ${this.isMatched(role, picked)
              ? html`<span class=${wizardFoundBadgeStyle}
                  >${t.foundBadge}</span
                >`
              : nothing}
          </label>
          <button
            class=${wizardSelectStyle}
            type="button"
            title=${option.description}
            @click=${(event: MouseEvent) =>
              this.openPickerMenu(event, role, properties, allowAuto, t)}
          >
            <span class=${muted ? wizardSelectPlaceholderStyle : ''}>
              ${selectedLabel}
            </span>
            <svg viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1 1l4 4 4-4"
                stroke="currentColor"
                stroke-width="1.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  private renderDisclosureHeader(
    title: string,
    disclosure: {
      description?: string;
      disabled: boolean;
      expanded: boolean;
      onToggle: () => void;
    }
  ): TemplateResult {
    return html`
      <div class=${wizardSectionHeaderStyle}>
        <div>
          <h2 class=${wizardSectionTitleStyle}>${title}</h2>
          ${disclosure.description
            ? html`<p class=${wizardSectionHintStyle}>
                ${disclosure.description}
              </p>`
            : nothing}
        </div>
        ${disclosure.disabled
          ? nothing
          : html`
              <button
                class=${wizardDisclosureButtonStyle}
                type="button"
                aria-expanded=${disclosure.expanded ? 'true' : 'false'}
                aria-label=${disclosure.expanded
                  ? dataViewI18nT('calendar.wizard.advancedCollapse', {
                      title,
                    })
                  : dataViewI18nT('calendar.wizard.advancedExpand', {
                      title,
                    })}
                data-open=${disclosure.expanded ? 'true' : 'false'}
                @click=${disclosure.onToggle}
              >
                ${this.renderDisclosureChevronIcon()}
              </button>
            `}
      </div>
    `;
  }

  private renderDisclosureChevronIcon(): TemplateResult {
    return html`
      <svg width="16" height="16" viewBox="0 0 16 16" focusable="false">
        <path
          d="M6 4l4 4-4 4"
          stroke="currentColor"
          stroke-width="1.8"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  private renderExpandableBody(
    expanded: boolean,
    renderBody: () => TemplateResult | typeof nothing
  ): TemplateResult {
    return html`
      <div
        class=${wizardExpandableBodyStyle}
        data-open=${expanded}
        aria-hidden=${expanded ? 'false' : 'true'}
        ?inert=${!expanded}
      >
        <div class=${wizardExpandableInnerStyle}>
          ${expanded ? renderBody() : nothing}
        </div>
      </div>
    `;
  }

  private roleColumnId(role: CalendarColumnRole): string | undefined {
    const roleColumns = {
      allDay: this.view.allDayCol$.value,
      attendees: this.view.attendeesCol$.value,
      calendarId: this.view.calendarIdCol$.value,
      description: this.view.descriptionCol$.value,
      end: this.view.endDateCol$.value,
      locationPhysical: this.view.locationPhysicalCol$.value,
      locationVirtual: this.view.locationVirtualCol$.value,
      recurrence: this.view.recurrenceCol$.value,
      recurrenceExdate: this.view.recurrenceExdateCol$.value,
      recurrenceId: this.view.recurrenceIdCol$.value,
      reminder: this.view.reminderCol$.value,
      start: this.view.startDateCol$.value,
      status: this.view.statusCol$.value,
      timezone: this.view.timezoneCol$.value,
      visibility: this.view.visibilityCol$.value,
    } satisfies Record<CalendarColumnRole, string | undefined>;
    return roleColumns[role];
  }

  private roleOption(role: CalendarColumnRole): CalendarRoleOption {
    const option = ROLE_CONFIG[role];
    const prefix = `calendar.wizard.role.${role}`;
    return {
      ...option,
      description: dataViewI18nT(`${prefix}.description` as DataViewI18nKey),
      label: dataViewI18nT(`${prefix}.label` as DataViewI18nKey),
      name: dataViewI18nT(`${prefix}.name` as DataViewI18nKey),
    };
  }

  private pickerPropertiesForRole(
    role: CalendarColumnRole
  ): CalendarSetupProperty[] {
    const properties = [...this.detectedColumns().byRole[role]];
    if (role !== 'start' && role !== 'end') return properties;
    const counterpart =
      role === 'start' ? this.picked.end.value : this.picked.start.value;
    return properties.filter(
      property =>
        property.id === this.picked[role].value || property.id !== counterpart
    );
  }

  private openPickerMenu(
    event: MouseEvent,
    role: CalendarColumnRole,
    properties: readonly CalendarSetupProperty[],
    allowAuto: boolean,
    t: CalendarWizardStrings
  ): void {
    const target = event.currentTarget as HTMLElement;
    const picked = this.picked[role];
    const creating = this.creating[role];
    const matchedId = this.detectedColumns().matches[role]?.id;
    const items = properties.map(property =>
      menu.action({
        isSelected: picked.value === property.id,
        name: `${property.name$.value}${
          property.id === matchedId ? t.matchedSuffix : ''
        }`,
        select: () => {
          picked.value = property.id;
          creating.value = null;
        },
      })
    );
    if (allowAuto) {
      items.push(
        menu.action({
          isSelected: !picked.value && creating.value === null,
          name: t.autoCreateLabel(this.roleOption(role).name),
          select: () => {
            picked.value = undefined;
            creating.value = null;
          },
        })
      );
    }
    items.push(
      menu.action({
        name: t.createNewColumn,
        select: () => {
          picked.value = undefined;
          creating.value = '';
        },
      })
    );
    popFilterableSimpleMenu(popupTargetFromElement(target), items);
  }

  private seedFromView(detected: CalendarDetectedColumns): void {
    const seededDateIds = new Set<string>();
    const startId =
      this.usableRoleColumnId('start', detected) ??
      this.matchId(detected.matches.start, seededDateIds);
    this.picked.start.value = startId;
    if (startId) seededDateIds.add(startId);
    const existingEndId = this.usableRoleColumnId('end', detected);
    const endId =
      existingEndId && !seededDateIds.has(existingEndId)
        ? existingEndId
        : this.matchId(detected.matches.end, seededDateIds);
    this.picked.end.value = endId;

    for (const role of Object.keys(ROLE_CONFIG) as CalendarColumnRole[]) {
      if (role === 'start' || role === 'end') continue;
      this.picked[role].value =
        this.usableRoleColumnId(role, detected) ?? detected.matches[role]?.id;
    }
    const hasConfiguredDates = Boolean(
      this.usableRoleColumnId('start', detected) ||
      this.usableRoleColumnId('end', detected)
    );
    const hasDateMatch = Boolean(
      detected.matches.start || detected.matches.end
    );
    this.datesMode.value =
      hasConfiguredDates || hasDateMatch ? 'existing' : 'auto';
    if (this.dateColumns().length === 1 && detected.matches.end) {
      this.singleDateRole.value = 'end';
    }
    this.advancedExpanded.value = false;
    this.datesExpanded.value = false;
    this.detailsExpanded.value = false;
    this.recurrenceExpanded.value = false;
    this.statusExpanded.value = false;
  }

  private matchId(
    property: CalendarSetupProperty | undefined,
    excludedIds: ReadonlySet<string>
  ): string | undefined {
    if (!property || excludedIds.has(property.id)) return undefined;
    return property.id;
  }

  private setOrCreateRole(role: CalendarColumnRole): void {
    const creating = this.creating[role].value;
    if (creating !== null) {
      this.createRoleColumn(
        role,
        creating.trim() || this.roleOption(role).name
      );
      return;
    }
    const picked = this.picked[role].value;
    if (picked) {
      if (
        (role === 'start' && picked === this.picked.end.value) ||
        (role === 'end' && picked === this.picked.start.value)
      ) {
        this.createRoleColumn(role);
        return;
      }
      this.view.setRoleColumn(role, picked);
      return;
    }
    this.createRoleColumn(role);
  }

  private t(): CalendarWizardStrings {
    return { ...defaultCalendarWizardStrings(), ...this.strings };
  }

  private usableRoleColumnId(
    role: CalendarColumnRole,
    detected = this.detectedColumns()
  ): string | undefined {
    const id = this.roleColumnId(role);
    if (!id) return undefined;
    return detected.byRole[role].some(property => property.id === id)
      ? id
      : undefined;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-calendar-setup-wizard': CalendarSetupWizard;
  }
}
